
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [isMobile, setIsMobile] = useState(false);
  
  const phrases = [
    "That Provides Quality Service in Your Budget",
    "Your Business, Our Digital Strategy",
    "We Build Brands that Build Trust",
    "Creative Video Ads that Convert",
    "Expert Web & App Development"
  ];
  
  useEffect(() => {
    setIsVisible(true);
    
    // Check if on mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Typing effect
    const ticker = setTimeout(() => {
      const currentIdx = loopNum % phrases.length;
      const fullText = phrases[currentIdx];
      
      setDisplayText(
        isDeleting
          ? fullText.substring(0, displayText.length - 1)
          : fullText.substring(0, displayText.length + 1)
      );
      
      // Adjust typing speed - faster deletion
      setTypingSpeed(isDeleting ? 30 : 150);
      
      // Handle complete typing or deleting
      if (!isDeleting && displayText === fullText) {
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && displayText === '') {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
      }
    }, typingSpeed);
    
    return () => {
      clearTimeout(ticker);
      window.removeEventListener('resize', checkMobile);
    };
  }, [displayText, isDeleting, loopNum, phrases, typingSpeed]);

  return (
    <div className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-white to-gray-50">
      {/* Wave Effect at Top */}
      <div className="absolute top-0 left-0 w-full overflow-hidden">
        <svg 
          className="relative block w-full h-[60px] md:h-[100px]" 
          data-name="Layer 1" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
        >
          <path 
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" 
            opacity=".25" 
            className="fill-blue-600"
          ></path>
          <path 
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" 
            opacity=".5" 
            className="fill-blue-500"
          ></path>
          <path 
            d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" 
            className="fill-blue-400"
          ></path>
        </svg>
      </div>

      {/* Background Animation */}
      <div className="absolute inset-0 z-0">
        <div className="absolute w-64 h-64 rounded-full bg-blue-100 -top-10 -left-10 animate-float opacity-70"></div>
        <div className="absolute w-72 h-72 rounded-full bg-orange-100 bottom-20 -right-10 animate-float opacity-60" style={{ animationDelay: "1s" }}></div>
        <div className="absolute w-48 h-48 rounded-full bg-blue-50 top-1/3 left-1/3 animate-float opacity-50" style={{ animationDelay: "2s" }}></div>
      </div>
      
      <div className="container mx-auto px-4 pt-28 pb-16 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Hero Content */}
          <div className={`w-full max-w-2xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800">
              Creative Digital Agency in Nepal
            </h1>
            
            <div className="h-16 md:h-16 overflow-visible">
              <p className="text-xl md:text-3xl lg:text-4xl font-semibold text-orange-500 break-words max-w-full">
                <span>{displayText}</span>
                <span className="cursor inline-block w-0.5 h-6 bg-orange-500 ml-1 animate-blink"></span>
              </p>
            </div>
            
            <p className="text-gray-600 text-base md:text-xl mt-8 mb-8">
              We help businesses grow their online presence with creative designs, 
              powerful web development, and strategic digital marketing solutions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/quote"
                className="cta-button group w-full sm:w-auto text-center"
                aria-label="Get a free quote for our digital services"
              >
                Get A Free Quote
                <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
              </Link>
              
              <Link 
                to="/projects"
                className="cta-button-secondary w-full sm:w-auto text-center"
                aria-label="View our portfolio of completed projects"
              >
                View Our Work
              </Link>
            </div>
          </div>
          
          {/* Hero Image - Hide on small screens */}
          <div className={`w-full max-w-xl hidden md:block transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-orange-500/20 rounded-2xl filter blur-xl transform -translate-y-4 translate-x-4"></div>
              <div className="glass-card rounded-2xl p-6 md:p-8 relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d" 
                  alt="DG IT Services - Web Development and Digital Marketing in Nepal" 
                  className="w-full h-auto rounded-xl shadow-lg"
                  loading="lazy"
                />
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  {[
                    { title: "Web Dev", icon: "💻", link: "/services/web-development" },
                    { title: "Marketing", icon: "📊", link: "/services/digital-marketing" },
                    { title: "Video Ads", icon: "🎬", link: "/services/video-ads" },
                    { title: "Design", icon: "✏️", link: "/services/design" }
                  ].map((service, index) => (
                    <Link
                      key={index}
                      to={service.link}
                      className="bg-white/80 backdrop-blur-sm rounded-lg shadow p-3 text-center transform transition-all duration-300 hover:scale-110 hover:shadow-md hover:bg-gradient-to-r hover:from-blue-50 hover:to-orange-50"
                    >
                      <div className="text-2xl mb-1">{service.icon}</div>
                      <h3 className="font-medium text-sm">{service.title}</h3>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Simplified Hero Image for Mobile */}
          <div className="block md:hidden w-full">
            <div className="grid grid-cols-2 gap-3 mt-6">
              {[
                { title: "Web Dev", icon: "💻", link: "/services/web-development" },
                { title: "Marketing", icon: "📊", link: "/services/digital-marketing" },
                { title: "Video Ads", icon: "🎬", link: "/services/video-ads" },
                { title: "Design", icon: "✏️", link: "/services/design" }
              ].map((service, index) => (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                >
                  <Link
                    to={service.link}
                    className="bg-white rounded-lg shadow p-4 text-center block hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="text-3xl mb-2">{service.icon}</div>
                    <h3 className="font-medium">{service.title}</h3>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
